/*
 * <BeDeskbar> -- React wrapper for the .be-deskbar CSS component.
 *
 * Recreates the R5 Deskbar's three sections (User's Guide
 * 01_basics/Basics03_Deskbar): the Be Menu leaf button, the Status View
 * clock well, and the running Application List. The leaf button toggles a
 * <BeMenu> passed via the `menu` prop -- that's the "Be Menu" the docs
 * describe.
 *
 * Matching CSS lives in components/deskbar.scss (loaded via index.scss);
 * as with the other components this file does not import CSS itself.
 */

import { type CSSProperties, type ReactNode, useEffect, useState } from "react";
import { type BeProps, beComponent } from "./create.tsx";
import { pixelText } from "./pixel-text.tsx";
import { BeTrackerClock } from "./TrackerClock.tsx";

/**
 * Mirrors the R5 `deskbar_location` enum (headers/interface/Deskbar.h). The
 * default desktop places the Deskbar expanded at the top-right corner.
 *
 * Note: only the default vertical "right-top" layout is fully styled today;
 * the value is surfaced as a data-attribute so the remaining orientations
 * can be added in CSS without an API change.
 */
export type BeDeskbarLocation = "top" | "bottom" | "left-top" | "right-top" | "left-bottom" | "right-bottom";

export type BeDeskbarApp = {
	/** Display name shown in the application list. */
	name: string;
	/** Icon source (any <img> src); falls back to a solid swatch if omitted. */
	icon?: string;
	/** Solid swatch color used when `icon` is absent. */
	color?: string;
	/** Click handler (e.g. bring the app to the front). */
	onSelect?: () => void;
};

export type BeDeskbarProps = BeProps<
	"div",
	{
		/** Running applications shown in the list section. */
		apps?: BeDeskbarApp[];

		/**
		 * The Be Menu, rendered next to the leaf when open. Typically a <BeMenu>.
		 * If omitted, the leaf is inert (no menu pops up).
		 */
		menu?: ReactNode;

		/** Start with the Be menu open (uncontrolled initial state). */
		defaultMenuOpen?: boolean;

		/** Replace the live clock with custom Status View content. */
		statusContent?: ReactNode;

		/** Replicant tray content shown left of the clock (e.g. a volume icon). */
		tray?: ReactNode;

		/** Corner/edge placement; mirrors `deskbar_location`. Default "right-top". */
		location?: BeDeskbarLocation;
	}
>;

/** The "Be OS" wordmark shown in the Deskbar header. */
function BeLogo() {
	return (
		<span className="be-deskbar__logo">
			{/* logo-be paints the dark leaf, so the run goes inside; deskbar.css
			    already targets `.be-deskbar__logo-be .be-pixel-text` to undo the
			    Gecko multiply, which would erase white ink (#407). */}
			<span className="be-deskbar__logo-be">{pixelText("Be")}</span>
			{pixelText("OS", "be-deskbar__logo-os")}
		</span>
	);
}

export const BeDeskbar = beComponent<BeDeskbarProps, HTMLDivElement>("BeDeskbar", (props, be) => {
	const { apps = [], menu, defaultMenuOpen = false, statusContent, tray, location = "right-top", scale, className, style, ...rest } = props;

	const [menuOpen, setMenuOpen] = useState(defaultMenuOpen);

	// Close the Be menu on Escape, matching desktop behavior.
	useEffect(() => {
		if (!menuOpen) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") setMenuOpen(false);
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [menuOpen]);

	const beClass = ["be-deskbar__be", menuOpen ? "is-open" : null].filter(Boolean).join(" ");

	return (
		<div data-location={location} {...rest} {...be("be-deskbar")}>
			<button
				type="button"
				className={beClass}
				aria-label="Be menu"
				aria-haspopup="menu"
				aria-expanded={menu ? menuOpen : undefined}
				onClick={() => menu && setMenuOpen((o) => !o)}
			>
				<BeLogo />
			</button>

			<div className="be-deskbar__status">
				{/* Recessed well holds the tray + clock; the grab sits outside it. */}
				<div className="be-deskbar__well">
					{tray && <div className="be-deskbar__tray">{tray}</div>}
					{statusContent ?? <BeTrackerClock />}
				</div>
				{/* Drag handle: decorative grip on the right of the status strip. */}
				<div className="be-deskbar__drag" aria-hidden="true" />
			</div>

			{apps.length > 0 && (
				<div className="be-deskbar__apps">
					{apps.map((app) => (
						<button type="button" className="be-deskbar__app" key={app.name} onClick={app.onSelect}>
							{app.icon ? (
								<img className="be-deskbar__app-icon" src={app.icon} alt="" />
							) : (
								<span className="be-deskbar__app-icon" style={app.color ? ({ "--be-app-color": app.color } as CSSProperties) : undefined} />
							)}
							{pixelText(app.name, "be-deskbar__app-name")}
						</button>
					))}
				</div>
			)}

			{menu && menuOpen && <div className="be-deskbar__menu">{menu}</div>}
		</div>
	);
});
